import { useEffect, useState } from 'react';
import { useUserRoles } from '@anytag/auth';
import { InfluencerNewProfileHeaderV2, namedOperations, useUpdateInternalMemoMutation } from '@anytag/graphql';
import { Button, Label, Textarea } from '@anytag/shared-atoms';
import { useQueryHelper } from '@anytag/shared-hooks';

export interface InfluencerProfileInternalMemoProps {
  influencerProfile: InfluencerNewProfileHeaderV2;
}

export const InfluencerProfileInternalMemo = ({
  influencerProfile: { id, internalMemo }
}: InfluencerProfileInternalMemoProps) => {
  const [value, setValue] = useState('');
  const { isAdminStaff } = useUserRoles();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { callUpdateInternalMemo, loading } = useUpdateInternalMemoMutation({
    refetchQueries: [namedOperations.Query.InfluencerProfileV2]
  });

  useEffect(() => {
    setValue(internalMemo || '');
  }, [internalMemo]);

  const handleSave = async () => {
    try {
      await callUpdateInternalMemo({ variables: { input: { id, internalMemo: value } } });
      enqueueSnackbar(t('Internal Memo updated successfully'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <div className="mt-4 box-border flex items-end gap-4 rounded-sm bg-white px-4 py-5 shadow-container">
      <div className="flex flex-1 flex-col">
        <Label
          title={
            <>
              <span className="font-semibold">{t('TextForm.Internal memo')}</span>
              &nbsp;
              <span className="text-primary">{t<string>('TextForm.Internal Memo Note')}</span>
            </>
          }
          help={t(
            isAdminStaff ? 'Tooltip.Internal Memo Help Admin&Staff' : 'Tooltip.Internal Memo Help External Users'
          )}
        />
        <Textarea
          minRows={2}
          maxRows={8}
          value={value}
          classNames={{ textarea: 'min-h-5' }}
          onChange={(e) => setValue(e.target.value)}
          autoSize
        />
      </div>

      <Button
        variant="solid"
        loading={loading}
        onClick={handleSave}
        title={t('Button.Save')}
        className="min-w-19.5"
        disabled={!value || internalMemo === value}
      />
    </div>
  );
};
